import {gql, useQuery} from '@apollo/client';
import {ValidationMessage} from 'common';
import {SelectField, SelectFieldProps} from 'evergreen-ui';
import React from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {isClinicBased, isClinicRequired, Referral} from 'referral';
import {Observable} from 'rxjs';
import {useObservable} from 'rxsupport';
import {useCurrentUser} from 'security';
import {OfferFormInput} from './OfferForm';
import {Clinic} from '..';

type LocationInput = {
    providerGuid: string;
};
type LocationResult = {
    clinic: Array<Clinic>;
};
type ClinicOption = {
    guid: string,
    name: string
};

const CLINIC_LOCATION_QUERY = gql`
query ($providerGuid: String!) {
  clinic(providerGuid: $providerGuid) {
    id
    guid
    location
    street
    city
    stateCode
    zipCode
  }
}
`;

type ClinicLocationInputProps = {
    referral: Referral;
    isSubmitting$: Observable<boolean>;
    requireClinicLocationInput?: boolean;
} & SelectFieldProps;

export const ClinicLocationInput = ({
                                        referral,
                                        isSubmitting$,
                                        requireClinicLocationInput = false,
                                        ...selectProps
                                    }: ClinicLocationInputProps) => {
    const currentUser = useCurrentUser()!;
    const {
        control,
        watch,
        formState: {
            errors,
        },
    } = useFormContext<OfferFormInput>();

    const providerGuid = currentUser.provider?.guid ?? watch('providerGuid')

    const {data, error, loading} = useQuery<LocationResult, LocationInput>(
        CLINIC_LOCATION_QUERY, {
            variables: {
                providerGuid: providerGuid ?? ""
            },
            skip: !providerGuid
        }
    );

    const clinics: Array<ClinicOption> = (loading || error ? [] : data?.clinic ?? []).map(c => ({
        "guid": c.guid,
        "name": `${c.city} - ${c.street}`
    }));
    clinics.sort((c1, c2) => c1.name.localeCompare(c2.name));

    const canProvideServiceInClinic = useWatch<OfferFormInput, 'provideServicesInClinic'>({
        control,
        name: 'provideServicesInClinic',
    });
    const isRequired = isClinicRequired(referral, requireClinicLocationInput) && canProvideServiceInClinic === 'true';
    const validationRules = {
        required: {
            message: 'This field is required.',
            value: isRequired,
        }
    };

    const sending = useObservable(isSubmitting$, false);

    return (
        <Controller
            name='clinicLocationGuid'
            control={control}
            rules={validationRules}
            render={({field: {onChange, onBlur, value}}) => {
                const props = {
                    maxWidth: '24em',
                    label: 'Clinic Location',
                    ...selectProps,
                    onChange,
                    onBlur,
                    value: value ? value : undefined,
                    disabled: !isRequired || loading || sending,
                    isInvalid: !!errors.clinicLocationGuid?.message,
                    validationMessage: <ValidationMessage error={errors.clinicLocationGuid}/>,
                }
                return <SelectField {...props}>
                    <option value=''/>
                    {
                        clinics.map((clinic) => <option key={clinic.guid} value={clinic.guid}>{clinic.name}</option>)
                    }
                    {/*// Remove due to BHPNPP-2515*/}
                    {/*{*/}
                    {/*    !isClinicBased(referral) &&*/}
                    {/*    <option value='__OTHER__'>Other</option>*/}
                    {/*}*/}
                </SelectField>;
            }}
        />
    );
}
